import dayjs, { Dayjs } from "dayjs";
import {
  SubjectType,
  Task,
  TaskPriority,
  TaskStatus,
} from "../../../models/workflowModels";
import { compassYellow } from "../../../styles";
import { taskStatusColor } from "../../../components/visuals/TaskKindIcon";
import { MESSAGES } from "../../../messages";

export enum TaskDateTone {
  DUE = "due",
  OVERDUE = "overdue",
  CREATED = "created",
}

export type TaskDateLabel = {
  text: string;
  tone: TaskDateTone;
};

export type ActionSummary = {
  open: number;
  inProgress: number;
  done: number;
};

const DAY_FORMAT = "D MMM";

const STATUS_RANK: Record<TaskStatus, number> = {
  [TaskStatus.STUCK]: 0,
  [TaskStatus.IN_PROGRESS]: 1,
  [TaskStatus.NOT_STARTED]: 2,
  [TaskStatus.COMPLETED]: 3,
};

const PRIORITY_RANK: Record<TaskPriority, number> = {
  [TaskPriority.HIGH]: 0,
  [TaskPriority.MEDIUM]: 1,
  [TaskPriority.LOW]: 2,
};

const FLAG_STATUS_WORDS: Record<TaskStatus, string> = {
  [TaskStatus.NOT_STARTED]: "Flagged",
  [TaskStatus.IN_PROGRESS]: "In progress",
  [TaskStatus.STUCK]: "Blocked",
  [TaskStatus.COMPLETED]: "Resolved",
};

const TASK_STATUS_WORDS: Record<TaskStatus, string> = {
  [TaskStatus.NOT_STARTED]: "Not started",
  [TaskStatus.IN_PROGRESS]: "In progress",
  [TaskStatus.STUCK]: "Stuck",
  [TaskStatus.COMPLETED]: "Complete",
};

export const SUBJECT_TYPE_LABELS: Record<SubjectType, string | null> = {
  [SubjectType.ENGAGEMENT_RECORDS]: "Engagement",
  [SubjectType.HOLDINGS]: "Holding",
  [SubjectType.VOTING_RECORDS]: "Vote",
  [SubjectType.COMPANY]: "Company",
  [SubjectType.FUNDS]: "Fund",
  [SubjectType.NONE]: null,
};

const SUBJECT_TYPES: string[] = Object.values(SubjectType);

export const isSubjectType = (value: string | null): value is SubjectType =>
  value !== null && SUBJECT_TYPES.includes(value);

const parseDate = (value: string | null | undefined): Dayjs | null => {
  if (!value || value === "-") return null;
  const date = dayjs(value);
  return date.isValid() ? date : null;
};

const dueTimestamp = (task: Task): number =>
  parseDate(task.date_due)?.valueOf() ?? Number.POSITIVE_INFINITY;

const createdTimestamp = (task: Task): number =>
  parseDate(task.date_created)?.valueOf() ?? 0;

const compareTasks = (a: Task, b: Task): number =>
  STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
  dueTimestamp(a) - dueTimestamp(b) ||
  PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
  createdTimestamp(b) - createdTimestamp(a);

export const visibleTasks = (tasks: Task[]): Task[] =>
  tasks.filter((task) => !task.is_archived).sort(compareTasks);

export const summariseTasks = (tasks: Task[]): ActionSummary => {
  const active = tasks.filter((task) => !task.is_archived);
  return {
    open: active.filter((task) => task.status !== TaskStatus.COMPLETED).length,
    inProgress: active.filter((task) => task.status === TaskStatus.IN_PROGRESS)
      .length,
    done: active.filter((task) => task.status === TaskStatus.COMPLETED).length,
  };
};

export const taskDateLabel = (task: Task): TaskDateLabel => {
  const due = parseDate(task.date_due);
  const today = dayjs().startOf("day");
  if (due && task.status !== TaskStatus.COMPLETED && due.isBefore(today)) {
    return {
      text: `${MESSAGES.ACTION_TRACKER.OVERDUE} ${today.diff(due, "day")}d`,
      tone: TaskDateTone.OVERDUE,
    };
  }
  if (due) {
    return {
      text: `${MESSAGES.ACTION_TRACKER.DUE} ${due.format(DAY_FORMAT)}`,
      tone: TaskDateTone.DUE,
    };
  }
  return {
    text: parseDate(task.date_created)?.format(DAY_FORMAT) ?? "",
    tone: TaskDateTone.CREATED,
  };
};

export const taskStatusWord = (task: Task): string =>
  task.is_flag
    ? FLAG_STATUS_WORDS[task.status]
    : TASK_STATUS_WORDS[task.status];

export const taskStatusDotColor = (task: Task): string =>
  task.is_flag && task.status === TaskStatus.NOT_STARTED
    ? compassYellow
    : taskStatusColor(task.status);
